import * as React from 'react'
import { useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'
import { Link, NavLink } from 'react-router-dom'

import { useApolloClient } from '@apollo/client/react'
import { clsx } from 'clsx'

import { AccountType } from '@buffer-mono/app-shell/src/common/events/types'
import {
  ChannelAvatar,
  Flex,
  Heading,
  IconButton,
  PageHeader,
  pageHeaderStyles,
  Popover,
  Separator,
  SettingsIcon,
  StarIcon,
  Text,
  CircleIcon,
  TooltipGroup,
  UnstyledButton,
  PencilIcon,
} from '@buffer-mono/popcorn'

import { SaveViewButton } from '~publish/components/MainSidebar/components/SavedViewsSection'
import {
  GetAccount,
  useHasOrganizationAction,
} from '~publish/contexts/AccountContext'
import { getChannelAdditionalInfo } from '~publish/helpers/channels'
import { getChannelNameForDisplay } from '~publish/helpers/getChannelNameForDisplay'
import { useIsMobileBreakpoint } from '~publish/hooks/useIsMobileBreakpoint'
import { MODALS, useOpenGlobalModal } from '~publish/hooks/useOpenGlobalModal'
import { actions as profilesActions } from '~publish/legacy/profiles/reducer'
import { channelSettings } from '~publish/legacy/routes'
import { useAppDispatch } from '~publish/legacy/store'
import { isViewOnlyPublishChannel } from '~publish/utils/permissions/viewOnlyPublish'

import { useSelectedChannel } from './ChannelContext'
import type { SelectedChannel } from './ChannelContext'
import {
  PostingGoalCoachMark,
  usePostingGoalCoachMark,
} from './PostingGoalCoachMark'
import { usePostingGoalAutoRefresh } from './usePostingGoalAutoRefresh'

import styles from './ChannelPageTitle.module.css'

const SEPARATOR_STYLE: React.CSSProperties & Record<`--${string}`, string> = {
  '--separator-gutter': 'var(--space-sm)',
}

export function ChannelPageTitle(): JSX.Element {
  const channel = useSelectedChannel()
  const canManageChannels = useHasOrganizationAction('manageChannels')
  const [isHovered, setIsHovered] = React.useState(false)
  const openPersonalAccountEditModal = useOpenPersonalAccountEditModal()
  const showPostingGoalInfo = channel?.postingGoal?.goal !== 0

  if (!channel) return <></>

  const secondaryChannelText = getChannelAdditionalInfo(channel)
  const isEditable = isChannelEditable(channel, canManageChannels)
  const channelName = getChannelNameForDisplay(channel)

  return (
    <PageHeader.TitleGroup
      leadingGap="comfortable"
      data-testid="channel-page-title"
    >
      <PageHeader.TitleLeading>
        <ChannelAvatar
          src={channel.avatar}
          alt={channel.service}
          channel={channel.service}
          showEditOverlay={isHovered}
          size="mid"
          className={styles.channelAvatar}
          onEditClick={
            isEditable
              ? (): void => openPersonalAccountEditModal(channel)
              : undefined
          }
        />
      </PageHeader.TitleLeading>
      <PageHeader.Title className={styles.channelName}>
        {isEditable ? (
          <UnstyledButton
            className={styles.editableChannelName}
            aria-label={`Edit ${channelName} channel connection`}
            onClick={(): void => openPersonalAccountEditModal(channel)}
            onMouseEnter={(): void => setIsHovered(true)}
            onMouseLeave={(): void => setIsHovered(false)}
          >
            <span translate="no">{channelName}</span>
          </UnstyledButton>
        ) : (
          <span translate="no">{channelName}</span>
        )}
      </PageHeader.Title>
      <PageHeader.TitleAdornments>
        <TooltipGroup>
          <SaveViewButton />
          {!isViewOnlyPublishChannel(channel) && (
            <IconButton
              id="settings-menu"
              variant="tertiary"
              label="Channel settings"
              tooltip="Channel Settings"
              size="small"
              data-tour-id="channel-settings"
              as={Link}
              to={`/channels/${channel.id}/settings`}
              className={pageHeaderStyles.hideOnNarrow}
            >
              <SettingsIcon />
            </IconButton>
          )}
        </TooltipGroup>
      </PageHeader.TitleAdornments>
      {!showPostingGoalInfo && secondaryChannelText && (
        <PageHeader.TitleDescription>
          {secondaryChannelText}
        </PageHeader.TitleDescription>
      )}
      {showPostingGoalInfo && (
        <PageHeader.TitleDescription>
          <PostingGoalInfo />
        </PageHeader.TitleDescription>
      )}
    </PageHeader.TitleGroup>
  )
}

const SHIMMER_DURATION = 1000

function PostingGoalInfo(): JSX.Element {
  const channel = useSelectedChannel()
  const canManageChannels = useHasOrganizationAction('manageChannels')
  const [isShimmering, setIsShimmering] = useState(false)
  const prevCounts = useRef<Record<string, number> | null>(null)
  const coachMark = usePostingGoalCoachMark()
  const isMobile = useIsMobileBreakpoint()
  const openGlobalModal = useOpenGlobalModal()

  usePostingGoalAutoRefresh(channel?.id, {
    skip: !channel?.postingGoal || channel.postingGoal.goal === 0,
  })

  const { postingGoal } = channel ?? {}
  // Only the goal's editing entry points hide for view-only access; the goal
  // progress itself stays visible.
  const isViewOnly = isViewOnlyPublishChannel(channel)

  useEffect(() => {
    if (!postingGoal) return

    if (
      prevCounts.current &&
      (postingGoal.sentCount > prevCounts.current.sentCount ||
        postingGoal.scheduledCount > prevCounts.current.scheduledCount)
    ) {
      setIsShimmering(true)

      const timer = setTimeout(function resetAnimation() {
        setIsShimmering(false)
      }, SHIMMER_DURATION)

      return (): void => clearTimeout(timer)
    }

    // Update previous values for next comparison
    prevCounts.current = {
      sentCount: postingGoal.sentCount,
      scheduledCount: postingGoal.scheduledCount,
    }
  }, [postingGoal])

  const clickSetPostingGoal = (): void => {
    if (!channel) return
    openGlobalModal(MODALS.postingGoalConfiguration, {
      channelId: channel.id,
      source: 'channel-page',
    })

    if (coachMark.isActive) {
      coachMark.dismiss()
    }
  }

  if (!channel) return <></>

  if (!postingGoal)
    return canManageChannels && !isViewOnly ? (
      <PostingGoalCoachMark>
        <UnstyledButton
          onClick={clickSetPostingGoal}
          className={styles.postingGoal}
        >
          <Text className={styles.postingGoalLabel}>+ Set a posting goal</Text>
        </UnstyledButton>
      </PostingGoalCoachMark>
    ) : (
      <></>
    )

  const StatusIcon =
    postingGoal.sentCount > postingGoal.goal ? StarIcon : CircleIcon

  const remainingCount = Math.max(
    postingGoal.goal - postingGoal.sentCount - postingGoal.scheduledCount,
    0,
  )

  const progressStages: Record<string, number> = {
    [styles.quarter]: postingGoal.goal / 4,
    [styles.half]: postingGoal.goal / 2,
    [styles.threeQuarters]: (postingGoal.goal * 3) / 4,
    [styles.full]: postingGoal.goal,
  }
  const progressStage = Object.keys(progressStages).findLast(
    (stage) => postingGoal.sentCount >= progressStages[stage],
  )

  return (
    <Popover>
      <Popover.Trigger>
        <UnstyledButton className={styles.postingGoal}>
          <div
            className={clsx(styles.postingGoalBadge, progressStage, {
              [styles.success]: postingGoal.sentCount > 0,
            })}
          >
            <StatusIcon />
          </div>

          <Text
            className={clsx(styles.postingGoalLabel, {
              [styles.shimmer]: isShimmering,
            })}
          >
            {postingGoal.sentCount}/{postingGoal.goal} posts sent this week
          </Text>
        </UnstyledButton>
      </Popover.Trigger>
      <Popover.Content
        arrow
        align="start"
        side={isMobile ? 'bottom' : 'right'}
        data-testid="posting-goal-info"
        className={styles.postingGoalPopover}
      >
        {!isViewOnly && (
          <IconButton
            as={NavLink}
            to={channelSettings.getRoute({
              channelId: channel.id,
              tab: 'posting-schedule',
            })}
            variant="tertiary"
            size="small"
            label="Edit posting goal"
            tooltip="Edit posting goal"
            className={styles.editPostingGoalButton}
          >
            <PencilIcon />
          </IconButton>
        )}
        <Heading as="h2" size="sm" className={styles.postingGoalHeading}>
          Posting Goal
        </Heading>
        <Text as="p" className={styles.postingGoalText}>
          {postingGoal.goal} {postingGoal.goal === 1 ? 'post' : 'posts'} per
          week
        </Text>
        <Separator decorative style={SEPARATOR_STYLE} />
        <Flex direction="row" gap="xs">
          <Text as="p">
            <b>{postingGoal.sentCount}</b> Sent
          </Text>
          <span>⋅</span>
          <Text as="p">
            <b>{postingGoal.scheduledCount}</b> Scheduled
          </Text>
          <span>⋅</span>
          <Text as="p">
            <b>{remainingCount}</b> To Do
          </Text>
        </Flex>
      </Popover.Content>
    </Popover>
  )
}

function useOpenPersonalAccountEditModal(): (channel: SelectedChannel) => void {
  const apollo = useApolloClient()
  const dispatch = useAppDispatch()
  const openGlobalModal = useOpenGlobalModal()

  return (channel: SelectedChannel): void => {
    const modalMap: Partial<Record<string, MODALS>> = {
      facebook: MODALS.facebookSelectAccountType,
      instagram: MODALS.instagramSelectAccountType,
    }
    const modal = modalMap[channel.service]

    if (!modal) {
      return
    }

    const accountTypeMap: Record<string, AccountType> = {
      profile: AccountType.personal,
      group: AccountType.group,
    }

    openGlobalModal(modal, {
      accountType: accountTypeMap[channel.type],
      isRefreshingConnection: true,
      channelId: channel.id,
      channelName: channel.name,
      channelAvatar: channel.avatar,
      channelUrl: channel.serverUrl ?? undefined,
      onContinue: () => {
        apollo.refetchQueries({ include: [GetAccount] })
        dispatch(
          profilesActions.fetchSingleProfile({
            profileId: channel.id,
            message: '',
          }),
        )
      },
    })
  }
}

function isChannelEditable(
  channel: SelectedChannel,
  canManageChannels = false,
): boolean {
  return (
    ((channel.service === 'instagram' && channel.type === 'profile') ||
      (channel.service === 'facebook' && channel.type === 'group')) &&
    canManageChannels
  )
}
